/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ActiveLinks, SameLevelInpageNavigation, SubLevelInpageNavigation } from '../../../types';
import NavItems from './NavItems';
import { useInPageDispatch, useInPageNavContext } from '../../context';
import { INPAGENAV_ACTIONS } from '../../reducers/inPageNavReducer';
import { getTopLevelActiveItemHref, hasSubLinks } from '../../utils/utils';
import { useElementHeight } from '../../hooks/use-element-height';
import { CustomTheme } from '../../../@types/theme';

interface IProps {
  visible: boolean;
  prioprityNavigation: SameLevelInpageNavigation[] | SubLevelInpageNavigation[];
  handleListRef?: (ref: React.RefObject<HTMLElement>) => void;
  activeLink?: number;
}

const StyledMorePopOver = styled.div<{
  $visible?: boolean;
  $hidePriorityDropDown?: boolean;
  $isTallerThanScreen?: boolean;
  $visibleHeight?: number;
}>`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: ${(props) => (props.$visible ? 'flex' : 'none')};
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    position: absolute;
    min-width: 278px;
    top: 100%;
    right: 0;
    background: ${({ theme }) => theme.colors.popoverBackground};
    padding: var(--spacing-relative-md);
    transition-timing-function: var(${({ theme }: { theme: CustomTheme }) => theme.easing});
    transition-duration: 125ms;
    transition-property: transform, opacity;
    overflow-y: auto;
    filter: ${({ theme }) => theme.colors.dropShadow};
    will-change: filter;
    ${(props) => (props.$visible ? 'opacity: 1; z-index: 1000;' : 'opacity: 0; z-index: -1;')};
    ${(props) => (props.$isTallerThanScreen ? `height: ${props.$visibleHeight}px;` : '')};
    border-radius: ${(props) => props.theme.colors.borderRadiusPopover};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}px) {
    ${(props) => props.$hidePriorityDropDown && `display: none;`};
  }
`;

const StyledNavList = styled.ul`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: stretch;
    flex-wrap: wrap;
    width: 100%;
    padding: 0;
    margin: 0;
  }
`;

export const StyledListItem = styled.li`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    position: relative;
    white-space: nowrap;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }
`;

const PopOverNav = (props: IProps) => {
  const { visible, prioprityNavigation, handleListRef, activeLink } = props;
  const popOverMenuRef = useRef<HTMLUListElement>(null);
  const { checkHeight, heightInfo } = useElementHeight(popOverMenuRef);

  const {
    parentPageInpageNavigation,
    sameLevelInpageNavigation,
    topLevelActiveLink,
    priorityNavActive,
  } = useInPageNavContext();
  const dispatch = useInPageDispatch();
  useEffect(() => {
    if (dispatch) {
      dispatch({
        type: INPAGENAV_ACTIONS.PRIORITY_NAV_LOADED,
        payload: {
          priorityNavLoaded: prioprityNavigation.length > 0,
        },
      });
    }
    // trigger height check when menu opens
    if (!heightInfo?.isTallerThanScreen) {
      checkHeight();
    }
  }, [prioprityNavigation]);

  useEffect(() => {
    if (popOverMenuRef?.current) handleListRef?.(popOverMenuRef);
  }, [popOverMenuRef, handleListRef]);

  const handleSelectedItem = ({ id, index, text, subLevelChildLabel }: ActiveLinks) => {
    // This is used to trigger the correct second level menu
    const findMatchingIndex = (): number | undefined => {
      if (!sameLevelInpageNavigation) return undefined;

      const matchIndex = sameLevelInpageNavigation.findIndex((item) => {
        const matchesText = item.fields?.Text === text;

        return matchesText;
      });

      return matchIndex >= 0 ? matchIndex : undefined;
    };

    const matchedIndex = findMatchingIndex();
    const indx = matchedIndex ?? index;
    if (dispatch) {
      const url = getTopLevelActiveItemHref(sameLevelInpageNavigation, indx);
      const hasSublinks = hasSubLinks(indx, sameLevelInpageNavigation).length > 0;
      dispatch({
        type: INPAGENAV_ACTIONS.TOP_LEVEL_ACTIVE_LINK,
        payload: {
          topLevelActiveLink: {
            id,
            index: indx,
            text,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            subLevelChildLabel: subLevelChildLabel || topLevelActiveLink?.subLevelChildLabel,
            href: url,
          },
          subNavActive: {
            index: indx,
            subLinks: {
              hasLinks: hasSublinks,
              active: index !== topLevelActiveLink?.index || Boolean(visible && hasSublinks),
              text: subLevelChildLabel || text,
            },
          },
        },
      });
    }
  };

  return (
    <StyledMorePopOver
      id="moreMenu"
      $visible={visible}
      $hidePriorityDropDown={prioprityNavigation?.length === 0 && priorityNavActive?.isDesktop}
      $isTallerThanScreen={heightInfo.isTallerThanScreen}
      $visibleHeight={heightInfo.visibleHeight}
    >
      <StyledNavList role="navigation" ref={popOverMenuRef} className="more-menu">
        {prioprityNavigation?.map((item, i) => {
          const { fields } = item;
          return (
            <NavItems
              // eslint-disable-next-line react/no-array-index-key
              key={`nav-item-${i}`}
              id={item.id}
              fields={fields}
              index={i}
              hasParentElement={!!parentPageInpageNavigation}
              handleSelectedItem={handleSelectedItem}
              fieldsLength={prioprityNavigation.length}
              subNavigation={fields?.subLinks}
              prioprityNavigationActiveIndex={
                activeLink && activeLink >= 0 ? activeLink : undefined
              }
            />
          );
        })}
      </StyledNavList>
    </StyledMorePopOver>
  );
};

export default PopOverNav;
